'use strict';

const settings = {
  accessToken: 'pk.eyJ1IjoiY2VydHN1cmVzaG9waWZ5IiwiYSI6ImNtNzF5NGdiNzA1NDAyanNpN2NtdnZlcWMifQ.N4H4tIydnWOmEsXtbRJ1Iw'
};

let map; // Declare map globally for easy access in the event listener
let geojsonFeatures = []; // Store GeoJSON features globally for easy access
let lastCoordinates = []; // Store the last coordinates for the search results
let directionsLayerFeatures = []; // Store the directions layer features

// Parse a metafield lat/long string into a [lon, lat] tuple, or null if invalid.
function parseMetafieldCoordinates(longitude, latitude) {
  if (longitude == null || latitude == null) return null;
  const lon = parseFloat(longitude);
  const lat = parseFloat(latitude);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
  if (lon < -180 || lon > 180 || lat < -90 || lat > 90) return null;
  if (lon === 0 && lat === 0) return null; // treat 0,0 as unset
  return [lon, lat];
}

async function fetchCoordinates(venue) {
  const params = new URLSearchParams({
    access_token: settings.accessToken,
    limit: 5,
    country: 'gb'
    // Do not restrict to place/locality
  });

  const geocodeUrl =
    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(venue)}.json?${params}`;

  const response = await fetch(geocodeUrl);
  const data = await response.json();

  if (!data.features || data.features.length === 0) {
    return null;
  }

  // Prefer exact / near-exact place_name or text matches
  const normalizedVenue = venue.trim().toLowerCase();

  const bestMatch =
    data.features.find(f =>
      f.place_name?.toLowerCase().includes(normalizedVenue) ||
      f.text?.toLowerCase() === normalizedVenue
    ) || data.features[0];

  return bestMatch.geometry.coordinates; // [longitude, latitude]
}

async function fetchDirections(startCoordinates = [], endCoordinates = []) {
  // Customize parameters to improve accuracy
  const params = new URLSearchParams({
    access_token: settings.accessToken,
    geometries: 'geojson',
    steps: true
  });

  // Construct the directions URL
  const directionsUrl = `https://api.mapbox.com/directions/v5/mapbox/driving-traffic/${startCoordinates};${endCoordinates}.json?${params}`;

  const response = await fetch(directionsUrl);
  const data = await response.json();

  if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
    return data.routes[0].geometry.coordinates; // Array of [longitude, latitude]
  }
  return null; // Handle if no directions found
}

async function drawDirections(coordinates, map) {

  // Update the directions source with the new coordinates
  map.getSource('directions')?.setData({
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: coordinates
    }
  });

  // Fit the map to the directions
  const bounds = coordinates.reduce((bounds, coord) => {
    return bounds.extend(coord);
  }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));


  map.fitBounds(bounds, {
    padding: 100
  });

  console.log('draw directions', coordinates);
}

async function setup() {
  mapboxgl.accessToken = settings.accessToken;

  // Retrieve and parse location data
  const locationData = JSON.parse(document.getElementById('map-locations').textContent);

  // Group locations by venue + (rounded) coordinates so two variants sharing a
  // venue label but with different addresses don't collapse onto one marker.
  const groupedLocations = locationData.reduce((acc, location) => {
    const metafieldCoords = parseMetafieldCoordinates(location.longitude, location.latitude);
    const coordKey = metafieldCoords
      ? `${metafieldCoords[0].toFixed(5)},${metafieldCoords[1].toFixed(5)}`
      : 'geocode';
    const key = `${location.venue}|${coordKey}`;
    if (!acc[key]) acc[key] = { venue: location.venue, metafieldCoords, events: [] };
    acc[key].events.push(location);
    return acc;
  }, {});

  for (const { venue, metafieldCoords, events } of Object.values(groupedLocations)) {
    // Prefer explicit lat/long metafields on the variant; fall back to geocoding.
    const coordinates = metafieldCoords || await fetchCoordinates(venue);

    if (!coordinates) {
      console.warn(`No coordinates resolved for venue "${venue}" (no lat/long metafields and geocoding failed).`);
      continue;
    }

    let address = '';
    const popupContent = events.map(event => {
      // Convert the date from yyyy-mm-dd to dd/mm/yyyy format
      const [year, month, day] = event.date.split('-'); // Split the date by '-'
      const formattedDate = `${day}/${month}/${year}`; // Rearrange to dd/mm/yyyy
      address = event?.full_address;

      // Generate the popup content
      return `
      <span class="pt-4 pb-4" style="display: inline-block;">${formattedDate}</span><br>
      `;
    }).join('<hr/>');


    // Add each event with its variant ID as a GeoJSON feature
    events.forEach(event => {
      geojsonFeatures.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: coordinates },
        properties: {
          venue: venue,
          variant_id: event.variant_id,
          popupContent: `<h3 class="h6">${venue}</h3><h5 class="h6 size--tiny mt-4 mb-4">${address}</h5>${popupContent}`
        }
      });
    });
  }

  // Initialize map with clustering
  map = new mapboxgl.Map({
    container: 'map',
    style: 'mapbox://styles/mapbox/streets-v12',
    center: [-2.1754278, 52.4971485], // Temporary center
    zoom: 5.5         // Default zoom level; will adjust based on venue data
  });

  map.addControl(new mapboxgl.NavigationControl());
  map.addControl(new mapboxgl.FullscreenControl());
  /* map.addControl(new mapboxgl.GeolocateControl({
    positionOptions: { enableHighAccuracy: true },
    trackUserLocation: true
  })); */
  map.addControl(new mapboxgl.ScaleControl());
  map.addControl(new mapboxgl.AttributionControl({ compact: true }));

  // Add GeoJSON source with clustering enabled
  map.on('load', () => {
    map.addSource('venues', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: geojsonFeatures
      },
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50
    });

    map.addSource('directions', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: []
        }
      }
    });

    map.addLayer({
      id: 'directions',
      source: 'directions',
      type: 'line',
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#9d072e',
        'line-width': 5
      }
    });

    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'venues',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#9d072e', 100, '#9d072e', 750, '#9d072e'
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          20, 100, 30, 750, 40
        ]
      }
    });
    map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'venues',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      },
      paint: {
        'text-color': '#fff' // Set the text color to white
      }
    });
    map.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'venues',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': '#9d072e',
        'circle-radius': 10,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#fff'
      }
    });
  });

  // Popup for unclustered points
  map.on('click', 'unclustered-point', (e) => {
    const coordinates = e.features[0].geometry.coordinates.slice();
    const { popupContent } = e.features[0].properties;

    new mapboxgl.Popup()
      .setLngLat(coordinates)
      .setHTML(popupContent)
      .addTo(map);
  });

  // Zoom into clusters when clicked
  map.on('click', 'clusters', (e) => {
    const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
    const clusterId = features[0].properties.cluster_id;
    map.getSource('venues').getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return;
      map.easeTo({
        center: features[0].geometry.coordinates,
        zoom: zoom
      });
    });
  });

  // Utility function to center the map on a given feature
  async function centerMapOnFeature(feature, map, _coordinates) {
    if (feature) {
      const { coordinates } = feature.geometry;

      // Center the map on the coordinates
      map.flyTo({
        center: coordinates,
        zoom: 8,
        essential: true // Ensures the animation works smoothly
      });

      lastCoordinates = _coordinates || [];

      console.log('center on coordinates', coordinates);
      console.log('center on _coordinates', _coordinates);

      if (coordinates && _coordinates) {
        const directions = await fetchDirections(_coordinates, coordinates);
        if (directions && directions.length >= 1) {
          drawDirections(directions, map);
        }
      }

    } else {

      console.warn('No location found for the provided feature.');
    }
  }

  // Event listener to center the map based on variant_id
  document.addEventListener('ajaxProduct:variantUpdated', (e) => {
    const variantId = `${e.detail.variant.id}`;

    // Find the coordinates for the selected variant_id
    const feature = geojsonFeatures.find(f => f.properties.variant_id === variantId);

    if (feature) {
      centerMapOnFeature(feature, map);
    } else {
      console.warn(`No location found for variant_id: ${variantId}`);
      console.log(e.detail);
    }
  });

  // Event listener to center the map based on the search results
  document.addEventListener('ajaxProduct:searchResults', (e) => {
    const results = e.detail.results;
    const coordinates = e.detail?.coordinates || []

    console.log('searching...', results);
    console.log('with coordinates...', coordinates);

    // Find the first match with a valid feature
    const firstMatch = results.length > 0
      ? geojsonFeatures.find(f => parseInt(f.properties.variant_id) === parseInt(results[0].variant_id))
      : null;


    if (firstMatch) {
      centerMapOnFeature(firstMatch, map, coordinates);
    } else {
      console.warn('No valid location found for the search results.');
      // Zoom map to default
      map.flyTo({
        center: [-2.1754278, 52.4971485], // Temporary center
        zoom: 5,
        essential: true
      });
    }
  });


  console.log('Mapbox Custom JS Loaded');
}

document.addEventListener('DOMContentLoaded', setup);
